import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SEOHead } from '../components/SEO';
import { submitContactForm } from '../services/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContactForm(formData);
      toast.success('Message sent successfully! We\'ll get back to you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      toast.error('Failed to send message. Please try again or contact us via WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: Mail, title: "Email", value: "humanonrent@gmail.com", link: "mailto:humanonrent@gmail.com" },
    { icon: Phone, title: "Phone", value: "+91 98105 02313", link: "tel:+919810502313" },
    { icon: MessageCircle, title: "WhatsApp", value: "Chat with us on WhatsApp", link: "https://wa.me/919810502313" },
    { icon: MapPin, title: "Address", value: "India" },
    { icon: Clock, title: "Working Hours", value: "Mon-Sat: 9 AM - 8 PM" }
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <SEOHead
        title="Contact HumanOnRent.AI - Support, Help & Inquiries | humanonrent@gmail.com"
        description="Contact HumanOnRent.AI for support, inquiries, or partnerships. Email: humanonrent@gmail.com, Phone: +91 98105 02313, WhatsApp support available."
        keywords="contact human on rent, humanonrent support, humanonrent email, humanonrent phone number, human on rent help"
        url="https://humanonrent.ai/contact"
      />
      <span className="sr-only-seo">contact human on rent, humanonrent support, humanonrent help</span>

      <Header />

      {/* Content */}
      <main className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-light text-gray-900 leading-tight mb-6">
              Get in <span className="font-medium">Touch</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Have questions or need assistance? We're here to help. Reach out to us anytime.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="premium-card p-8">
              <h2 className="text-2xl font-medium text-gray-900 mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="input-premium w-full"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="input-premium w-full"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="input-premium w-full"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="input-premium w-full"
                    placeholder="How can we help?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="input-premium w-full resize-none py-3"
                    placeholder="Describe your query..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-premium w-full flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      Send Message
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-medium text-gray-900 mb-6">Contact Information</h2>
              <div className="space-y-6">
                {contactInfo.map((info, idx) => (
                  <div key={idx} className="premium-card p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">{info.title}</h3>
                      {info.link ? (
                        <a href={info.link} className="text-gray-600 hover:text-amber-600 transition-colors">
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-gray-600">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 premium-card p-6 bg-[#111827] text-white">
                <h3 className="font-medium mb-3">Emergency Support</h3>
                <p className="text-gray-400 text-sm mb-4">
                  For urgent matters or safety concerns, use the SOS feature in your dashboard or call our 24/7 helpline.
                </p>
                <p className="text-amber-400 font-semibold">Emergency: +91 98105 02313</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
